import { ChildrenPanel } from '@/components/value-chain/explorer/ChildrenPanel';
import { HierarchyPanel } from '@/components/value-chain/explorer/HierarchyPanel';
import { InsightsRow } from '@/components/value-chain/explorer/InsightsRow';
import { NodeInspector } from '@/components/value-chain/explorer/NodeInspector';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import { ancestorsOf } from '@/components/value-chain/treeIndex';
import { shortName } from '@/components/value-chain/nodeVisuals';
import type { ValueChainGraph, ValueChainInspectorTab, ValueChainNode } from '@/types/valueChain';

type Props = {
  graph: ValueChainGraph;
  index: TreeIndex;
  root: ValueChainNode;
  focus: ValueChainNode;
  selected: ValueChainNode;
  expanded: Set<string>;
  inspectorTab: ValueChainInspectorTab;
  starred: boolean;
  onToggleExpanded: (id: string) => void;
  onExpandAll: () => void;
  onFocus: (id: string) => void;
  onSelect: (id: string) => void;
  onDrill: (id: string) => void;
  onInspectorTabChange: (tab: ValueChainInspectorTab) => void;
  onToggleStar: () => void;
};

export function HierarchyWorkspace({
  graph,
  index,
  root,
  focus,
  selected,
  expanded,
  inspectorTab,
  starred,
  onToggleExpanded,
  onExpandAll,
  onFocus,
  onSelect,
  onDrill,
  onInspectorTabChange,
  onToggleStar,
}: Props) {
  const usedIn = ancestorsOf(index, selected.id)
    .slice(0, -1)
    .map((node) => shortName(node.name))
    .join(' · ') || graph.name;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-5">
      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-3">
        <HierarchyPanel
          root={root}
          index={index}
          expanded={expanded}
          focusId={focus.id}
          selectedId={selected.id}
          onToggle={onToggleExpanded}
          onSelect={onFocus}
          onExpandAll={onExpandAll}
        />
        <ChildrenPanel
          parent={focus}
          index={index}
          selectedId={selected.id}
          onSelect={onSelect}
          onDrill={onDrill}
        />
        <NodeInspector
          node={selected}
          index={index}
          tab={inspectorTab}
          onTabChange={onInspectorTabChange}
          starred={starred}
          onToggleStar={onToggleStar}
          usedIn={usedIn}
        />
      </div>
      <InsightsRow node={selected} index={index} />
    </div>
  );
}
